import { makeUseAxios } from 'axios-hooks'
import axiosInstance from '@/app/services/axiosInstance.service'
import { env } from '@/env'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '@/app/store/createStore'
import { useNotification, useToggle, useSystemMessage } from '@/shared/hooks'
import { getError } from '@/shared/helpers'
import { ExportingEvent } from 'devextreme/ui/data_grid'
import { Workbook } from 'exceljs'
import { exportDataGrid } from 'devextreme/excel_exporter'
import { saveAs } from 'file-saver-es'
import {
	ConfigDiasSurtido,
	DocsPendientes,
	FiltersSurtidoTiendas,
	ItemStock,
	ParameterSGI,
	SurtidoTiendaLog,
	Tienda,
	DataDetalleSurtidoTienda,
	LogResultDocument,
	ResultDocument,
	ResultExec,
	WhsBufferPercentConfiguration,
	REPLENISHMENT_TYPES
} from '@/features/inventoryAssortment/types/SurtidoTiendas.types'
import {
	resetSurtidoTiendasData,
	changeStep,
	setSurtidoTiendasData,
	addDocLog,
	setPenetracionBuffer
} from '@/features/inventoryAssortment/slices/SurtidoTiendas.slices'
import { format } from 'date-fns'
import { getServiceLayerError } from '@/app/service_layer/helpers/getServiceLayerError'
import { IWarehouse } from '@/features/operationStatus/types/warehouse'
import {
	useCreateStockTransferMutation,
	useCreateStockTransferRequestMutation,
	useLazyGetAllWarehousesQuery,
	useLazyGetStockTransferQuery,
	useLoginMutation
} from '@/features/operationStatus/service/serviceLayerService'
import { StockTransfer, StockTransferLines } from '@/features/operationStatus/types/stockTransfer'
import { StockTransferType } from '@/features/operationStatus/types/packingList'
import { useState } from 'react'

export const BUFFER_ORIGIN = {
	SYNC_FLOW: 2,
	ONE_BEAT: 1
} as const

export interface UpdateBufferRequestBody {
	almacenes: Array<string>
	origen: number
}
const useSurtidoTiendas = () => {
	const { user } = useSelector((state: RootState) => state.auth)
	const CompanyDB = process.env.REACT_APP_SAP_COMPANY_TEST ?? env.REACT_APP_SAP_COMPANY_TEST
	const UPDATE_BUFFER = process.env.REACT_APP_UPDATE_BUFFER ?? env.REACT_APP_UPDATE_BUFFER
	const USE_NORMAL_PROCEDURE =
		process.env.REACT_APP_USE_NORMAL_PROCEDURE ?? env.REACT_APP_USE_NORMAL_PROCEDURE
	const [loading, setLoading] = useToggle(false)
	const [openConfirm, setOpenConfirm] = useToggle(false)
	const [generatingDocs, setGeneratingDocs] = useState(false)
	const [lstTiendasPlaza, setLstTiendasPlaza] = useState<Tienda[]>([])

	const [executeLogin] = useLoginMutation()
	const [executeGetAllWarehousesRequest] = useLazyGetAllWarehousesQuery()
	const [executeGetStockTransfer] = useLazyGetStockTransferQuery()

	const {
		surtidoTiendas: { data: dataSurtido, step: selectedTab, logDocs, whsBufferPercents, ...filters }
	} = useSelector((state: RootState) => state.repoInventory)

	const useAxios = makeUseAxios({
		axios: axiosInstance(process.env.REACT_APP_API_WMS ?? (env.REACT_APP_API_WMS as string))
	})

	const dispatch = useDispatch()
	const notification = useNotification()
	const systemMessage = useSystemMessage()

	const [{ loading: loadingTiendas }, executeGetTiendas] = useAxios<Tienda[]>(
		{ url: '/asistente-reposicion/tiendas', method: 'get' },
		{ manual: true }
	)

	const [{ loading: loadingBuffer }, excuteActualizarBuffer] = useAxios<ResultExec>(
		{ url: '/asistente-reposicion/buffer', method: 'put' },
		{ manual: true }
	)

	const [{ loading: loadingDocsPendientes }, excuteGetDocsPendientes] = useAxios<DocsPendientes[]>(
		{ url: '/asistente-reposicion/documentos-pendientes/tiendas/logs', method: 'post' },
		{ manual: true }
	)
	const [generatingDocsPends, setGeneratingDocsPends] = useToggle(false)

	// Asistente de Reposición
	const [{ loading: loadingDetalleNormal }, executeDetalleNormal] = useAxios<
		DataDetalleSurtidoTienda[]
	>({ url: '/asistente-reposicion/tiendas', method: 'post' }, { manual: true })

	const [{ loading: loadingDetalleOneBeat }, executeDetalleOneBeat] = useAxios<
		DataDetalleSurtidoTienda[]
	>({ url: '/asistente-reposicion-one-beat', method: 'post' }, { manual: true })

	const [{ loading: loadingDetalleSyncFlow }, executeDetalleSyncFlow] = useAxios<
		Array<DataDetalleSurtidoTienda>
	>(
		{
			url: '/asistente-reposicion-sync-flow',
			method: 'post'
		},
		{ manual: true }
	)

	const [{}, excuteGetParametro] = useAxios<ParameterSGI>(
		{ url: '/asistente-reposicion/parametros', method: 'post' },
		{ manual: true }
	)

	const [{}, excuteAddDocPendiente] = useAxios<ResultExec>(
		{ url: '/asistente-reposicion/documentos-pendientes/tiendas', method: 'post' },
		{ manual: true }
	)

	const [executeCreateStockTransfer] = useCreateStockTransferMutation()
	const [executeCreateStockTransferRequest] = useCreateStockTransferRequestMutation()

	const [dataSurtidosByUser, setDataSurtidosByUser] = useState<SurtidoTiendaLog[]>()
	const [{ loading: searching }, executeGetSurtidosByUser] = useAxios<SurtidoTiendaLog[]>(
		{ url: '/asistente-reposicion/logs/lista', method: 'post' },
		{ manual: true }
	)
	const [{}, executeAddLog] = useAxios<SurtidoTiendaLog[]>(
		{ url: '/asistente-reposicion/logs', method: 'post' },
		{ manual: true }
	)

	/**
	 * Este hook trae toda la configuracion de las tiendas
	 */
	const [{}, executeGetWhsAssormentConfiguration] = useAxios<ConfigDiasSurtido[]>(
		{
			url: '/asistente-reposicion/dias',
			method: 'get'
		},
		{ manual: true }
	)

	const onChangePreviousTab = async () => {
		dispatch(changeStep(selectedTab - 1))
	}

	const onChangeNextTab = async () => {
		dispatch(changeStep(selectedTab + 1))
	}

	const onReset = async () => {
		dispatch(resetSurtidoTiendasData())
		systemMessage.remove()
	}

	const onChangePlaza = async (code: string) => {
		setLstTiendasPlaza([])
		await executeGetTiendas({ params: { plaza: code } })
			.then(({ data }) => {
				setLstTiendasPlaza(data)
			})
			.catch((error) => {
				notification.error('No se obtuvo información de la tiendas.\nError: ' + getError(error))
			})
	}

	const checkAddExtraSurtido = (
		whsCode: string,
		assormentConfigData: ConfigDiasSurtido[],
		dateToCheck: Date
	) => {
		const config = assormentConfigData.find((c) => c.Almacen === whsCode)
		if (!config) return false

		const dayOfWeek = dateToCheck.getDay() // 0 = Domingo, 1 = Lunes, etc.
		switch (dayOfWeek) {
			case 1:
				return config.Lunes === 'Y' && config.MasLunes === 'Y'
			case 2:
				return config.Martes === 'Y' && config.MasMartes === 'Y'
			case 3:
				return config.Miercoles === 'Y' && config.MasMiercoles === 'Y'
			case 4:
				return config.Jueves === 'Y' && config.MasJueves === 'Y'
			case 5:
				return config.Viernes === 'Y' && config.MasViernes === 'Y'
			case 6:
				return config.Sabado === 'Y' && config.MasSabado === 'Y'
			case 0:
				return config.Domingo === 'Y' && config.MasDomingo === 'Y'
			default:
				return false
		}
	}

	/**
	 * Function to calculate replenishment, delivered quantity, and denied quantity
	 * based on various input parameters and logic for inventory management.
	 *
	 * @param Buffer - The buffer stock or safety stock level required.
	 * @param IsAccesory - Indicates whether the item is an accessory.
	 * @param RotacionCliente - The rotation value associated with the client.
	 * @param MinimoAccesorio - The minimum accessory quantity.
	 * @param RotacionCalculada - The calculated rotation value.
	 * @param PromedioVentas - The average sales value.
	 * @param ExistenciasBodega - The stock quantity available in the warehouse.
	 * @param UltimaVenta - The quantity of the last sale.
	 * @param ExistenciaTienda - The stock quantity currently available in the store.
	 * @param RotaAlmacen - The rotation value of the warehouse.
	 * @param RotaReal - The actual rotation value.
	 * @param TipoRep - The type of replenishment strategy to apply (e.g., "OneBeat", "R").
	 * @param Day7Qty - The quantity sold in the past 7 days.
	 * @param AddExtra - Should we add an extra piece to the replenishment?.
	 * @returns An array containing three numbers:
	 *                     - reponer (quantity to replenish),
	 *                     - entregado (quantity delivered),
	 *                     - negado (quantity denied).
	 */
	const CalculateValuesRepo = (
		Buffer: number,
		IsAccesory: boolean,
		RotacionCliente: number,
		MinimoAccesorio: number,
		RotacionCalculada: number,
		PromedioVentas: number,
		ExistenciasBodega: number,
		UltimaVenta: number,
		ExistenciaTienda: number,
		RotaAlmacen: number,
		RotaReal: number,
		TipoRep: string,
		Day7Qty: number,
		AddExtra: boolean = false
	) => {
		let reponer: number = 0
		let entregado: number = 0
		let negado: number = 0
		let DiferenciaRotacion: number

		switch (TipoRep) {
			case REPLENISHMENT_TYPES.SYNC_FLOW:
			case REPLENISHMENT_TYPES.ONE_BEAT:
				/*REPONER*/
				if (ExistenciaTienda > Buffer) {
					reponer = 0
				} else if (ExistenciaTienda === Buffer) {
					reponer = AddExtra ? 1 : 0
				} else {
					reponer = Buffer - ExistenciaTienda
					if (AddExtra) reponer += 1
				}

				/*ENTREGADO*/
				if (reponer > 0 && reponer < ExistenciasBodega) entregado = reponer
				else if (reponer > 0 && ExistenciasBodega > 0) entregado = ExistenciasBodega

				/*NEGADO*/
				if (reponer > 0) negado = reponer - entregado

				break
			case 'R':
				Buffer = MinimoAccesorio
				/*REPONER*/
				if (RotacionCliente != 99.99) DiferenciaRotacion = RotacionCliente - RotacionCalculada
				else DiferenciaRotacion = 1

				if (IsAccesory && RotacionCliente != 99.99) {
					if (ExistenciaTienda < MinimoAccesorio) {
						reponer = Math.round(RotacionCliente * MinimoAccesorio)
						if (reponer === 0) reponer = 1
					}
				} else {
					/*Validacion para Articulos con rotacion 99.99*/
					if (RotacionCliente != 99.99) {
						reponer = Math.round(DiferenciaRotacion * PromedioVentas)
						if (reponer === 0) reponer = 1
					} else {
						if (RotaAlmacen == 99.99 && RotaReal != 99.99) {
							/*EL ALMACEN MANEJA LA ROTACION 99.99 POR LO TANTO SURTE LA VENTA y se repone en base a la venta*/
							reponer = UltimaVenta
							if (reponer === 0) reponer = 1
						} else if (Buffer > ExistenciaTienda) {
							/*REPOSICION SEGUN BUFFER*/
							reponer = Buffer - ExistenciaTienda
						}
					}
				}

				/*ENTREGADO*/
				if (reponer > 0 && reponer < ExistenciasBodega) entregado = reponer
				else if (reponer > 0 && ExistenciasBodega > 0) entregado = ExistenciasBodega

				/*NEGADO*/
				if (reponer > 0) negado = reponer - entregado
				break
			default:
				/*REPONER*/
				if (ExistenciaTienda > Buffer) {
					reponer = 0
				} else if (ExistenciaTienda === Buffer) {
					reponer = AddExtra ? 1 : 0
				} else {
					reponer = Day7Qty
					if (AddExtra) reponer += 1
				}

				// reponer = Day7Qty

				/*ENTREGADO*/
				if (reponer > 0 && reponer < ExistenciasBodega) entregado = reponer
				else if (reponer > 0 && ExistenciasBodega > 0) entregado = ExistenciasBodega

				/*NEGADO*/
				if (reponer > 0) negado = reponer - entregado

				break
		}

		return [reponer, entregado, negado]
	}

	const generateReposicionesPendientes = async (docsPendientes: DocsPendientes[]) => {
		setGeneratingDocsPends()
		try {
			for await (const doc of docsPendientes) {
				const dataStockTransfer = await executeGetStockTransfer({
					DocNum: doc.DocNumTransfer.toString()
				})
					.unwrap()
					.then((data) => {
						return data
					})
					.catch((error) => {
						notification.error(
							'No se obtivo informacion de la transferencia ' +
								doc.DocNumTransfer.toString() +
								'.\nError: ' +
								getServiceLayerError(error)
						)
						return undefined
					})

				if (dataStockTransfer === undefined) {
					return false
				}

				const dataSolicitud: StockTransfer = {
					DocDate: dataStockTransfer.DocDate,
					TaxDate: dataStockTransfer.TaxDate,
					PriceList: dataStockTransfer.PriceList,
					Comments: dataStockTransfer.Comments,
					FromWarehouse: doc.WhsPuente,
					ToWarehouse: doc.WhsCode,
					U_GSP_MOVESTOCK: dataStockTransfer.U_GSP_MOVESTOCK,
					StockTransferLines: dataStockTransfer.StockTransferLines.map((item) => ({
						ItemCode: item.ItemCode,
						Quantity: item.Quantity
					})),
					U_BYS_CodeDocBaseStr: dataStockTransfer.U_BYS_CodeDocBaseStr
				}
				const resultSolicitud: ResultDocument = await createStockTransfer(
					dataSolicitud,
					'stockTransferRequest'
				)
				if (resultSolicitud.DocNum === 0) {
					notification.error(
						'No fue posible generar la solicitud de traslado de ' +
							doc.WhsFrom +
							' al ' +
							doc.WhsCode +
							'.\nError: ' +
							(resultSolicitud.Error ? resultSolicitud.Error : 'Sin Contexto')
					)
					setGeneratingDocsPends()
					return false
				}
			}
			setGeneratingDocsPends()
			return true
		} catch (error) {
			setGeneratingDocsPends()
			return false
		}
	}

	/**
	 * Asynchronous function to retrieve detailed stock replenishment data for stores based on various filter parameters.
	 *
	 * @param values - The input parameters containing store details, segments, sale date, and base type for stock calculation.
	 * @returns This function executes API calls and handles calculation logic for generating detailed stock replenishment data.
	 *
	 * The function uses different calculation bases:
	 * - `SyncFlow`: Retrieves and processes data from the SyncFlow system.
	 * - `OneBeat`: Retrieves and processes data from the OneBeat system; can be combined with 'Ambos' (both SyncFlow and OneBeat).
	 * - `Normal`: Retrieves data from traditional sources; can also be combined with 'Ambos' for dual data sources.
	 *
	 * Handles the following:
	 * - Transforms input data into the correct API-compatible format.
	 * - Manages API responses and errors for different data sources (`SyncFlow`, `OneBeat`, `Normal`).
	 * - Performs stock calculations using `CalculateValuesRepo` for replenishment details such as restock quantities, deliveries, and shortages.
	 * - Generates unique IDs for stock replenishment records by concatenating warehouse and item data.
	 * - Fetches additional buffer configuration data for warehouses and applies it to the generated stock replenishment data.
	 * - Dispatches the processed stock replenishment data to the application's Redux store and manages UI transitions.
	 *
	 * Error handling:
	 * - Displays error messages for failed API calls or calculation issues via the notification system.
	 * - Adds system messages for specific conditions, such as when no items are found for replenishment.
	 */
	const getDetalleSurtido = async (values: FiltersSurtidoTiendas) => {
		try {
			const dataSurtido: DataDetalleSurtidoTienda[] = []
			const dataSend = {
				fventa: values.FechaVenta,
				almacenes: "('" + values.Tiendas.toString().replaceAll(',', "','") + "')",
				incluirModa: values.Segmentos.includes('MODA') ? 'Y' : 'N',
				incluirAcc: values.Segmentos.includes('ACCESORIOS') ? 'Y' : 'N',
				incluirLinea: values.Segmentos.includes('LINEA') ? 'Y' : 'N',
				incluirOtros: values.Segmentos.includes('OTROS') ? 'Y' : 'N'
			}

			console.info('Usar Procedimiento Normal', USE_NORMAL_PROCEDURE)

			if (values.Base === REPLENISHMENT_TYPES.SYNC_FLOW && USE_NORMAL_PROCEDURE === 'N') {
				const dataSyncFlow = await executeDetalleSyncFlow({
					data: {
						...dataSend,
						fventa: format(dataSend.fventa, 'yyyy-MM-dd'),
						incluirAcc: dataSend.incluirAcc,
						incluirOtros: dataSend.incluirOtros
					}
				})
					.then(({ data }) => {
						console.info('Reposición de SyncFlow')
						return data
					})
					.catch(({ status, response }) => {
						if (status === 409) systemMessage.add(response.data.message)
						else notification.error(response.data.message)
						return []
					})
				dataSurtido.push(...dataSyncFlow)
			}

			if (
				values.Base === REPLENISHMENT_TYPES.ONE_BEAT ||
				values.Base === REPLENISHMENT_TYPES.BOTH
			) {
				/*CALCULO OBTENIDO CON ONE BEAT */
				const dataOneBeat = await executeDetalleOneBeat({
					data: {
						...dataSend,
						fventa: format(dataSend.fventa, 'yyyy-MM-dd'),
						incluirAcc: values.Base === REPLENISHMENT_TYPES.BOTH ? 'N' : dataSend.incluirAcc,
						incluirOtros: values.Base === REPLENISHMENT_TYPES.BOTH ? 'N' : dataSend.incluirOtros
					}
				})
					.then(({ data }) => {
						console.info('Reposición Base', values.Base)
						return data
					})
					.catch(({ status, response }) => {
						if (status === 409) systemMessage.add(response.data.message)
						else notification.error(response.data.message)
						return []
					})
				dataSurtido.push(...dataOneBeat)
			}

			if (
				values.Base === REPLENISHMENT_TYPES.NORMAL ||
				values.Base === REPLENISHMENT_TYPES.BOTH ||
				USE_NORMAL_PROCEDURE === 'Y'
			) {
				const fechaVenta: Date = new Date(dataSend.fventa)
				if (values.Base === REPLENISHMENT_TYPES.BOTH) fechaVenta.setDate(fechaVenta.getDate() - 1)
				const dataNormal = await executeDetalleNormal({
					data: {
						...dataSend,
						fventa: format(fechaVenta, 'yyyy-MM-dd'),
						incluirLinea: values.Base === REPLENISHMENT_TYPES.BOTH ? 'N' : dataSend.incluirLinea,
						incluirModa: values.Base === REPLENISHMENT_TYPES.BOTH ? 'N' : dataSend.incluirModa
					}
				})
					.then(({ data }) => {
						console.info('Reposición Base', USE_NORMAL_PROCEDURE === 'Y' ? 'Normal' : values.Base)
						return data
					})
					.catch((error) => {
						notification.error(getError(error))
						return []
					})
				dataSurtido.push(...dataNormal)
			}

			if (dataSurtido.length > 0) {
				/**Se destructura la configuracion del buffer */
				const whsBufferPercents: Array<WhsBufferPercentConfiguration> = []
				const { data: configDiasSurtidoData } = await executeGetWhsAssormentConfiguration()
				if (configDiasSurtidoData) {
					const whsBufferPercentsLines: Array<WhsBufferPercentConfiguration> =
						configDiasSurtidoData.map((item) => ({
							almacen: item.Almacen,
							linea: item.PorcLinea,
							moda: item.PorcModa,
							accesorios: item.PorcAccesorios,
							otros: item.PorcOtros
						}))
					whsBufferPercents.push(...whsBufferPercentsLines)
				}

				for (let i = 0; i < dataSurtido.length; i++) {
					const isHeadOrShoulder =
						dataSurtido[i].HsbtSegmento === 'Head' || dataSurtido[i].HsbtSegmento === 'Shoulder'
					const isLineaOrModa =
						dataSurtido[i].TipoArticulo === 'Linea' || dataSurtido[i].TipoArticulo === 'Moda'
					const addExtra =
						isLineaOrModa && isHeadOrShoulder && configDiasSurtidoData
							? checkAddExtraSurtido(dataSurtido[i].WhsCode, configDiasSurtidoData, new Date())
							: false
					const calculateData: number[] = CalculateValuesRepo(
						dataSurtido[i].Buffer,
						dataSurtido[i].TipoArticulo === 'Accesorio' ? true : false,
						dataSurtido[i].MinStock,
						dataSurtido[i].MinOrder,
						dataSurtido[i].RotaCalc,
						dataSurtido[i].SalesAvg,
						dataSurtido[i].WhsFromOnHand,
						dataSurtido[i].Day7Qty,
						dataSurtido[i].WhsOnHand + dataSurtido[i].WhsOnHandTransito,
						dataSurtido[i].UUBysRotacion,
						dataSurtido[i].RotaReal,
						dataSurtido[i].TipoRep,
						dataSurtido[i].Day7Qty,
						addExtra
					)
					dataSurtido[i].Reponer = calculateData[0]
					dataSurtido[i].Entregado = calculateData[1]
					dataSurtido[i].Negado = calculateData[2]
					dataSurtido[i].AplicaSurtidoExtra = addExtra ? 'Y' : 'N'

					/*GENERA UNICO ID CONCATENANDO (WhsFrom,WhsCode,ItemCode) */
					const ID: string =
						dataSurtido[i].WhsFrom + dataSurtido[i].WhsCode + dataSurtido[i].ItemCode
					dataSurtido[i].ID = ID.replaceAll('-', '')
				}

				dispatch(
					setSurtidoTiendasData({
						data: dataSurtido,
						logDocs: [],
						step: selectedTab,
						whsBufferPercents,
						...values
					})
				)
				onChangeNextTab()
			} else systemMessage.add('No se encontraron artículos para surtir')
		} catch (error) {
			notification.error(getError(error))
		}
	}

	const updateBuffer = async (data: UpdateBufferRequestBody) => {
		const { data: actualizarBuffer } = await excuteActualizarBuffer({
			data
		})

		if (actualizarBuffer.status != 200) {
			throw new Error(
				actualizarBuffer.mensaje ? actualizarBuffer.mensaje : 'Error al actualizar buffer'
			)
		}

		return actualizarBuffer
	}

	const onSubmitFilters = async (values: FiltersSurtidoTiendas) => {
		setLoading()
		try {
			dispatch(resetSurtidoTiendasData())
			dispatch(setPenetracionBuffer(values.penetracionBuffer))
			if (values.Segmentos.length === 0) throw new Error('Seleccione por lo menos un segmento')
			if (values.Plaza === 0) throw new Error('Seleccione una plaza')
			if (values.Tiendas.length === 0) throw new Error('Seleccione por lo menos una tienda')
			if (values.ListaPrecios === 0) throw new Error('Seleccione una lista de precios')

			/*INICIO DE SESION SAP*/
			await executeLogin({
				UserName: values.UserSAP,
				Password: values.PwdSAP,
				CompanyDB: CompanyDB
			})
				.unwrap()
				.catch((error) => {
					throw new Error('Inicio de sesión SAP no válida.\nError: ' + getServiceLayerError(error))
				})

			/*
				ACTUALIZAR BUFFER
			*/

			console.info('Actualizar Buffer', UPDATE_BUFFER)

			if (UPDATE_BUFFER === 'Y') {
				await updateBuffer({
					almacenes: values.Tiendas,
					origen:
						values.Base === REPLENISHMENT_TYPES.SYNC_FLOW
							? BUFFER_ORIGIN.SYNC_FLOW
							: BUFFER_ORIGIN.ONE_BEAT
				})
				console.info('Actualizado Buffer - Origen', values.Base)
			}

			/*CONSULTAR REPOSICIONES PENDIENTES*/
			const { data: docsPendientes } = await excuteGetDocsPendientes({
				data: {
					empresa: CompanyDB,
					almacenes: values.Tiendas
				}
			})

			if (docsPendientes.length > 0) {
				const generatePendientes: boolean = await generateReposicionesPendientes(docsPendientes)
				if (!generatePendientes) {
					throw new Error('Error al ejecutar las reposiciones pendiente\nIntenta de nuevo')
				} else {
					notification.success('Reposiciones pendientes generadas correctamente')
				}
			}

			/*OBTENER DETALLE DE SURTIDO*/
			await getDetalleSurtido(values)
			setLoading()
		} catch (error) {
			notification.error(getError(error))
			setLoading()
		}
	}

	const isSelectableRowDataSurtido = (item: DataDetalleSurtidoTienda) => {
		if (Number(item.Entregado) > 0) return true
		else {
			notification.warning('La linea no puede ser seleccionada porque la cantidad de Entrega es 0')
			return false
		}
	}

	const onGenerateSurtido = async (selectedRowKeys: any) => {
		setOpenConfirm()
		setLoading()
		try {
			/*OBTENER DATA LINEAS SELECCIONADAS*/
			const surtir: DataDetalleSurtidoTienda[] = dataSurtido.filter((item) =>
				selectedRowKeys.includes(item.ID)
			)
			if (surtir.length > 0) {
				setGeneratingDocs(true)
				const { data: configDiasSurtidoData } = await executeGetWhsAssormentConfiguration()
				/*realizar un datatable con el stock actual en los almacenes centrales de los artículos que se van a tratar*/
				let itemsStock: ItemStock[] = surtir.map((item) => ({
					ItemCode: item.ItemCode,
					WhsFrom: item.WhsFrom,
					WhsFromOnHand: item.WhsFromOnHand
				}))

				itemsStock = itemsStock.filter((item, index) => {
					return itemsStock.indexOf(item) === index
				})

				/*OBTENER ORDEN DE PREFERENCIA DE SURTIDO DE LAS TIENDAS*/
				const dataWarehouses: IWarehouse[] = await executeGetAllWarehousesRequest({
					params: {
						$filter:
							"WarehouseCode eq '" +
							filters.Tiendas.toString().replaceAll(',', "' or WarehouseCode eq '") +
							"'",
						$orderby: 'U_GSP_ITORDER'
					}
				})
					.unwrap()
					.then((data) => {
						return data.value
					})
					.catch((error) => {
						const errorMessage = getServiceLayerError(error)
						notification.error('No se obtuvo la preferencia de surtido.\nError: ' + errorMessage)
						return []
					})

				/*recorrer los almacenes/tiendas seleccionados y ordenados por prioridad*/
				for await (const warehouse of dataWarehouses) {
					/*filtrar solo las líneas del almacén actual y que se tienen que reponer*/
					const surtirTemp: DataDetalleSurtidoTienda[] = surtir
						.filter((item) => item.WhsCode === warehouse.WarehouseCode && Number(item.Reponer) > 0)
						.sort(
							(a, b) =>
								a.WhsFrom.localeCompare(b.WhsFrom) ||
								a.AlmPuenteVal.localeCompare(b.AlmPuenteVal) ||
								a.ItemCode.localeCompare(b.ItemCode)
						)
					if (surtirTemp.length > 0)
						await tratarLineas(itemsStock, surtirTemp, configDiasSurtidoData)
				}
				setGeneratingDocs(false)
			} else {
				systemMessage.add('No se encontraron reposiciones por efectuar.')
			}
			setLoading()
			onChangeNextTab()
		} catch (error) {
			setLoading()
			setGeneratingDocs(false)
			notification.error(getServiceLayerError(error))
		}
	}

	const tratarLineas = async (
		itemsStock: ItemStock[],
		data: DataDetalleSurtidoTienda[],
		configDiasSurtidoData?: ConfigDiasSurtido[]
	) => {
		let reponer_array: number[]
		let dataTraslado: StockTransfer | undefined
		let linesTraslado: StockTransferLines[] = []
		let whsFrom: string = ''
		let whsPuente: string = ''
		let whsTo: string = ''

		for await (const item of data) {
			if (whsFrom != item.WhsFrom) {
				if (whsFrom != '' && dataTraslado != undefined) {
					dataTraslado.StockTransferLines = linesTraslado
					/*GENERA TRANSFERENCIA DE STOCK*/
					await guardarStockTransfer(whsFrom, whsPuente, whsTo, dataTraslado)
				}
				const d = new Date()
				const CodeDocBaseStr: string = 'RT-WMS-' + format(d, 'yyyyMMddHmmssSSS')

				/*obtener razones de movimiento desde parametros GPS */
				const { data: MOVRAZON } = await excuteGetParametro({
					data: { nombre: 'MOVRAZON', aplicacion: 'GS_BYES_REP', empresa: CompanyDB }
				})

				/*iniciar nuevo data traslado*/
				linesTraslado = []
				dataTraslado = {
					DocDate: format(filters.FechaConta, 'yyyy-MM-dd'),
					TaxDate: format(filters.FechaDoc, 'yyyy-MM-dd'),
					PriceList: filters.ListaPrecios,
					FromWarehouse: item.WhsFrom,
					U_GSP_MOVESTOCK: MOVRAZON.Value.toString(),
					Comments: filters.Comentario,
					StockTransferLines: linesTraslado,
					U_BYS_CodeDocBaseStr: CodeDocBaseStr
				}
				whsFrom = item.WhsFrom
				whsPuente = item.AlmPuenteVal
				whsTo = item.WhsCode
			}

			/*obtener el stock actual del almacén central*/
			const stock = itemsStock.find(
				(i) => i.ItemCode === item.ItemCode && i.WhsFrom === item.WhsFrom
			)

			if (stock === undefined) {
				notification.error(
					'No se ha encontrado stock en' + item.WhsFrom + ' para el artículo ' + item.ItemCode
				)
				return false
			}

			const isHeadOrShoulder = item.HsbtSegmento === 'Head' || item.HsbtSegmento === 'Shoulder'
			const isLineaOrModa = item.TipoArticulo === 'Linea' || item.TipoArticulo === 'Moda'
			const addExtra =
				isLineaOrModa && isHeadOrShoulder && configDiasSurtidoData
					? checkAddExtraSurtido(item.WhsCode, configDiasSurtidoData, new Date())
					: false
			/*calcular la reposición de esta línea con el stock actual del almacén central (Porque el stock del almacén central cambia continuamente)*/
			reponer_array = CalculateValuesRepo(
				item.Buffer,
				item.TipoArticulo === 'Accesorio' ? true : false,
				item.MinStock,
				item.MinOrder,
				item.RotaCalc,
				item.SalesAvg,
				stock.WhsFromOnHand,
				item.Day7Qty,
				item.WhsOnHand + item.WhsOnHandTransito,
				item.UUBysRotacion,
				item.RotaReal,
				item.TipoRep,
				item.Day7Qty,
				addExtra
			)
			//const Reponer: number = reponer_array[0]
			const Entregado: number = reponer_array[1]
			const Negado: number = reponer_array[2]

			/*añadir la línea en el traslado si se tiene que reponer
        si la línea tiene ctd. entregado > 0 , validar la ctd. en stock del almacén central*/
			if (Entregado > 0) {
				linesTraslado.push({
					ItemCode: item.ItemCode,
					Quantity: Entregado,
					WarehouseCode: item.AlmPuenteVal,
					DistributionRule: item.Centro,
					U_U_BYS_EXISTENCIA: item.WhsOnHand.toString(),
					U_U_BYS_UVENTA: item.Day7Qty.toString(),
					U_U_BYS_NEGADO: Negado.toString(),
					U_U_BYS_ROTACION: Math.round(item.RotaCalc).toString()
				})
				/*MUY IMPORTANTE RESTAR DEL STOCK DEL ALMACÉN CENTRAL*/
				stock.WhsFromOnHand -= Entregado
			}
		}

		if (whsFrom != '' && dataTraslado != undefined && linesTraslado.length > 0) {
			dataTraslado.StockTransferLines = linesTraslado
			/*GENERA TRANSFERENCIA DE STOCK*/
			await guardarStockTransfer(whsFrom, whsPuente, whsTo, dataTraslado)
		}
		return true
	}

	const guardarStockTransfer = async (
		whsFrom: string,
		whsPuente: string,
		whsTo: string,
		dataTraslado: StockTransfer
	) => {
		const logResultDoc: LogResultDocument = {
			WhsCodeFrom: whsFrom,
			AlmPuenteVal: whsPuente != whsTo ? whsPuente : '',
			WhsCode: whsTo
		}
		const resultTransfer: ResultDocument = await createStockTransfer(dataTraslado, 'stockTransfer')

		if (resultTransfer.DocNum != 0) {
			/*GENERO CORRECTAMENTE LA TRANSFERENCIA*/
			if (whsPuente != whsTo) {
				/*CREAR SOLICITUD DE TRASLADO DEL PUENTE A LA TIENDA*/
				const dataSolicitud: StockTransfer = {
					DocDate: dataTraslado.DocDate,
					TaxDate: dataTraslado.TaxDate,
					PriceList: dataTraslado.PriceList,
					Comments: dataTraslado.Comments,
					FromWarehouse: whsPuente,
					ToWarehouse: whsTo,
					U_GSP_MOVESTOCK: dataTraslado.U_GSP_MOVESTOCK,
					StockTransferLines: dataTraslado.StockTransferLines.map((item) => ({
						ItemCode: item.ItemCode,
						Quantity: item.Quantity
					})),
					U_BYS_CodeDocBaseStr: dataTraslado.U_BYS_CodeDocBaseStr
				}
				const resultSolicitud: ResultDocument = await createStockTransfer(
					dataSolicitud,
					'stockTransferRequest'
				)

				if (resultSolicitud.DocNum != 0) {
					/*SOLICITUD GENERADA CORRECTAMENTE*/
					logResultDoc.DocNum = resultTransfer.DocNum
					logResultDoc.SolDocNum = resultSolicitud.DocNum
				} else {
					await excuteAddDocPendiente({
						data: {
							documentos: 'SolTrasl',
							baseCode: dataTraslado.U_BYS_CodeDocBaseStr,
							empresa: CompanyDB,
							usuario: filters.UserSAP,
							ubicacion: 'Reposicion',
							whsCode: whsTo,
							whsPuente: whsPuente,
							whsFrom: whsFrom
						}
					})
					logResultDoc.DocNum = resultTransfer.DocNum
					logResultDoc.Error = resultSolicitud.Error
				}
			} else {
				logResultDoc.DocNum = resultTransfer.DocNum
			}
			/*AGREGAR LOG DE DOCUMENTOS DE SURTIDO DE TIENDAS*/
			await executeAddLog({
				data: {
					AlmOrigen: logResultDoc.WhsCodeFrom,
					AlmDestino: logResultDoc.WhsCode,
					Puente: logResultDoc.AlmPuenteVal != '' ? logResultDoc.AlmPuenteVal : null,
					FechaContabilidad: dataTraslado.DocDate,
					FechaDocumento: dataTraslado.TaxDate,
					BaseCodeStr: dataTraslado.U_BYS_CodeDocBaseStr,
					TransferDocNum: logResultDoc.DocNum,
					SolTrasladoDocNum: logResultDoc.SolDocNum ? logResultDoc.SolDocNum : null,
					Usr: user?.Id,
					Fecha: format(new Date(), 'yyyy-MM-dd'),
					Hora: format(new Date(), 'HH:mm:ss'),
					Empresa: CompanyDB
				}
			})
		} else {
			logResultDoc.Error = resultTransfer.Error
		}

		dispatch(addDocLog(logResultDoc))
	}

	const createStockTransfer = async (dataDoc: StockTransfer, type: StockTransferType) => {
		if (type === 'stockTransferRequest') {
			return await executeCreateStockTransferRequest({ ...dataDoc })
				.unwrap()
				.then((data) => {
					return { DocNum: data.DocNum, Error: '' }
				})
				.catch((error) => {
					return { DocNum: 0, Error: error.data.error.message }
				})
		} else {
			return await executeCreateStockTransfer({ ...dataDoc })
				.unwrap()
				.then((data) => {
					return { DocNum: data.DocNum, Error: '' }
				})
				.catch((error) => {
					return { DocNum: 0, Error: error.data.error.message }
				})
		}
	}

	const onExport = (evt: ExportingEvent<LogResultDocument, any>) => {
		const workbook = new Workbook()
		const worksheet = workbook.addWorksheet('SURTIDO')
		exportDataGrid({
			component: evt.component,
			worksheet,
			autoFilterEnabled: true
		}).then(() => {
			workbook.xlsx.writeBuffer().then((buffer) => {
				saveAs(
					new Blob([buffer], { type: 'application/octet-stream' }),
					'Docs Surtido Tiendas ' + format(new Date(), 'yyyy-MM-dd') + '.xlsx'
				)
			})
		})
		evt.cancel = true
	}

	const getSurtidosByUser = async (finicial: Date, ffinal: Date) => {
		try {
			if (finicial > ffinal)
				throw new Error('La Fecha inicio debe ser menor o igual a la fecha final')

			await executeGetSurtidosByUser({
				data: {
					Empresa: CompanyDB,
					Usr: user?.Id,
					FechaIni: format(finicial, 'yyyy-MM-dd'),
					FechaFin: format(ffinal, 'yyyy-MM-dd')
				}
			})
				.then(({ data }) => setDataSurtidosByUser(data))
				.catch(({ error }) => {
					setDataSurtidosByUser([])
					notification.error('No fue posible obtener informacion. Error: ' + getError(error))
				})
		} catch (error) {
			notification.error(getError(error))
		}
	}

	const validateWhsBufferPercent = (
		line: DataDetalleSurtidoTienda,
		warehousesConfig?: Array<WhsBufferPercentConfiguration>
	) => {
		if (!warehousesConfig) return false
		const whsConfig = warehousesConfig.find((whs) => whs.almacen === line.WhsCode)
		console.log(whsConfig)
		if (!whsConfig) return false

		switch (line.TipoArticulo) {
			case 'Linea':
				return line.PenetracionBuffer >= whsConfig.linea
			case 'Moda':
				return line.PenetracionBuffer >= whsConfig.moda
			case 'Accesorio':
				return line.PenetracionBuffer >= whsConfig.accesorios
			case 'Otros':
				return line.PenetracionBuffer >= whsConfig.otros
			default:
				return false
		}
	}

	return {
		loading,
		loadingTiendas,
		lstTiendasPlaza,
		onChangePlaza,
		loadingBuffer,
		loadingDetalleNormal,
		loadingDetalleOneBeat,
		loadingDetalleSyncFlow,
		user,
		filters,
		logDocs,
		dataSurtido,
		selectedTab,
		onChangePreviousTab,
		onReset,
		onSubmitFilters,
		generatingDocs,
		onGenerateSurtido,
		openConfirm,
		isSelectableRowDataSurtido,
		setOpenConfirm,
		onExport,
		loadingDocsPendientes,
		generatingDocsPends,
		dataSurtidosByUser,
		searching,
		getSurtidosByUser,
		whsBufferPercents,
		validateWhsBufferPercent
	}
}
export default useSurtidoTiendas
